/**
 * ToggleSwitchHandler — 비동기 toggle-switch 처리.
 *
 * admin.components.toggle-switch (PHP) 가 렌더한 `.admin-toggle-switch-form` 폼들을
 * fetch(async/await)로 가로채서 **페이지 새로고침 없이** 상태를 전환한다.
 *
 * 패턴:
 *   1. submit preventDefault
 *   2. $confirm 있으면 Modal.confirm → 거부 시 중단
 *   3. 버튼 disabled + 로딩 텍스트
 *   4. fetch POST (Accept: application/json)
 *   5. 성공 → DOM 업데이트(클래스·hint·aria-pressed) + Toast.success
 *   6. 오류 → Toast.error / Modal.alert (criticalError 옵션)
 *   7. 항상 → 버튼 re-enable
 *
 * 서버 컨트롤러는 expectsJson()=true 일 때 { result, msg, enabled } 를 반환해야 한다.
 *
 * 사용:
 *   ToggleSwitchHandler.init(root);          // root 내 모든 .admin-toggle-switch-form 적용
 *   ToggleSwitchHandler.init(root, options); // 커스텀 콜백 포함
 *
 * 카탈로그: docs/03-constants/components.md (ToggleSwitchHandler)
 * 규칙: 상태변경/CRUD 작업은 Toast 패턴 사용 — docs/01-rules/must-check.md E-2
 */

import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

export interface ToggleSwitchHandlerOptions {
  /**
   * 토글 성공 후 콜백. DOM 업데이트(클래스·hint) 뒤에 호출된다.
   * result.enabled = 새 상태.
   */
  onSuccess?: (form: HTMLFormElement, result: ToggleResult) => void;
  /**
   * 오류 시 Toast.error 외에 추가 처리.
   */
  onError?: (form: HTMLFormElement, error: unknown) => void;
  /**
   * true 면 오류 시 Toast.error 대신 Modal.alert (치명적 오류에 사용).
   */
  criticalError?: boolean;
}

export interface ToggleResult {
  result: "success" | "fail";
  msg: string;
  enabled: boolean;
}

const HINT_ON  = "켜짐";
const HINT_OFF = "꺼짐";
const LOAD_TEXT = "처리 중…";

export const ToggleSwitchHandler = {
  /**
   * root 내 모든 .admin-toggle-switch-form 에 비동기 처리 바인딩.
   * 이미 data-ts-toggle-bound 가 있는 폼은 건너뜀.
   */
  init(root: HTMLElement | Document = document, options: ToggleSwitchHandlerOptions = {}): void {
    root.querySelectorAll<HTMLFormElement>(".admin-toggle-switch-form").forEach((form) => {
      if (form.dataset.tsTogleBound === "1") return;
      form.dataset.tsTogleBound = "1";
      this._bindForm(form, options);
    });
  },

  _bindForm(form: HTMLFormElement, options: ToggleSwitchHandlerOptions): void {
    form.addEventListener("submit", async (e: SubmitEvent) => {
      e.preventDefault();

      const btn   = form.querySelector<HTMLButtonElement>(".admin-toggle-switch");
      const hint  = btn?.querySelector<HTMLElement>(".admin-toggle-switch__hint");
      const confirmMsg = form.dataset.confirm;

      // --- 확인 모달 (위험 액션: OFF 시 라우트 404)
      if (confirmMsg) {
        const ok = await Modal.confirm(confirmMsg);
        if (!ok) return;
      }

      // --- 로딩 상태
      if (btn) {
        btn.disabled = true;
        btn.dataset.prevHint = hint?.textContent ?? "";
      }
      if (hint) hint.textContent = LOAD_TEXT;

      const restore = (): void => {
        if (btn) btn.disabled = false;
        if (hint) hint.textContent = btn?.dataset.prevHint ?? "";
      };

      // --- fetch
      try {
        const fd = new FormData(form);
        const res = await fetch(form.action, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
          credentials: "same-origin",
        });

        let data: ToggleResult;
        try {
          data = (await res.json()) as ToggleResult;
        } catch {
          restore();
          this._showError(form, "응답을 파싱할 수 없습니다.", options);
          return;
        }

        if (data.result !== "success") {
          restore();
          this._showError(form, data.msg || "토글에 실패했습니다.", options);
          return;
        }

        // --- DOM 업데이트
        this._applyState(form, btn, hint, data.enabled);

        Toast.success(data.msg || (data.enabled ? "활성화했습니다." : "비활성화했습니다."));
        options.onSuccess?.(form, data);
      } catch (err: unknown) {
        restore();
        const msg = err instanceof Error ? err.message : "네트워크 오류가 발생했습니다.";
        this._showError(form, msg, options);
        options.onError?.(form, err);
      }
    });
  },

  _applyState(
    form: HTMLFormElement,
    btn: HTMLButtonElement | null,
    hint: HTMLElement | null,
    enabled: boolean,
  ): void {
    // 버튼 클래스 + aria
    if (btn) {
      btn.classList.toggle("is-on", enabled);
      btn.classList.toggle("is-off", !enabled);
      btn.setAttribute("aria-pressed", enabled ? "true" : "false");
      btn.disabled = false;
    }
    // hint 텍스트 (현재 상태 표시)
    if (hint) hint.textContent = enabled ? HINT_ON : HINT_OFF;
    // form data-confirm 갱신 (끄는 방향일 때만 표시)
    if (enabled) {
      const label = form.closest("[data-feature-key]")?.querySelector(".admin-features__item-label")?.textContent ?? "이 기능";
      form.dataset.confirm = `'${label}' 을 끄면 관련 라우트가 404 가 됩니다. 계속할까요?`;
    } else {
      delete form.dataset.confirm;
    }
    // 부모 li 상태 클래스 업데이트 (선 색 등)
    const li = form.closest<HTMLElement>("[data-feature-key]");
    if (li) {
      li.classList.toggle("is-on", enabled);
      li.classList.toggle("is-off", !enabled);
    }
  },

  _showError(form: HTMLFormElement, msg: string, options: ToggleSwitchHandlerOptions): void {
    if (options.criticalError) {
      void Modal.alert(msg);
    } else {
      Toast.error(msg);
    }
  },
};
